import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

// API
import { baseApi } from "./api/baseApi.js";

// Slices - FIX: Import the actual auth reducer from the slice
import authReducer from "./slice/authSlice.js"; // Changed from "../contexts/AuthContext.js"
// import uiReducer from "./slices/uiSlice.js";

// Middleware
import { authMiddleware } from "./middleware/auth.middleware.js";
// import { analyticsMiddleware } from "./middleware/analyticsMiddleware.js";

// Configuration
import { APP_CONFIG } from "../constants/app.js";

// ===== STORE CONFIGURATION =====
export const store = configureStore({
  reducer: {
    // RTK Query API
    [baseApi.reducerPath]: baseApi.reducer,

    // App slices
    auth: authReducer,
    // ui: uiReducer,
  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Serialization checks
      serializableCheck: {
        ignoredActions: [
          // Ignore RTK Query actions
          baseApi.util.resetApiState.type,
          "persist/PERSIST",
          "persist/REHYDRATE",
        ],
        ignoredActionsPaths: ["meta.arg", "payload.timestamp"],
        ignoredPaths: ["api.queries", "api.mutations"],
      },

      // Immutability checks
      immutableCheck: {
        warnAfter: 128, // Warn if check takes longer than 128ms
        ignoredPaths: ["api"],
      },

      // Thunk configuration
      thunk: {
        extraArgument: {
          // Add extra arguments for thunks if needed
          apiBaseUrl: APP_CONFIG.API_BASE_URL,
        },
      },
    })
      .concat(baseApi.middleware) // RTK Query middleware
      .concat(authMiddleware.middleware), // Custom auth middleware
  // .concat(analyticsMiddleware.middleware), // Analytics middleware

  // DevTools configuration - FIX: Safely access authReducer.actions
  devTools: APP_CONFIG.REDUX_DEVTOOLS && {
    name: APP_CONFIG.APP_NAME,
    trace: APP_CONFIG.IS_DEVELOPMENT,
    traceLimit: 25,

    // Custom action creators for better debugging - FIX: Import actions from slice
    actionCreators: {
      // RTK Query utilities
      resetApiState: baseApi.util.resetApiState,
      invalidateAllTags: () =>
        baseApi.util.invalidateTags([
          "User",
          "Dashboard",
          "Report",
          "Settings",
          "Notification",
        ]),

      // Auth utilities - FIX: Import actions properly
      logout: () => ({ type: "auth/logout" }),
      updateActivity: () => ({ type: "auth/updateLastActivity" }),
    },

    // Enhanced features
    features: {
      pause: true,
      lock: true,
      persist: true,
      export: true,
      import: "custom",
      jump: true,
      skip: true,
      reorder: true,
      dispatch: true,
      test: true,
    },
  },

  // Preloaded state (if any)
  preloadedState: {},

  // Enhancers (for additional store capabilities)
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers({
      // Configure enhancers
      autoBatch: { type: "tick" }, // Batch actions within same tick
    }),
});

// ===== SETUP RTK QUERY LISTENERS =====
// This enables automatic refetching, polling, etc.
setupListeners(store.dispatch);

// ===== STORE UTILITIES =====

/**
 * Get current authentication status - FIX: Add error handling
 */
export const getAuthStatus = () => {
  try {
    const state = store.getState();
    return {
      isAuthenticated: state.auth?.isAuthenticated || false,
      user: state.auth?.user || null,
      role: state.auth?.user?.role || null,
      permissions: state.auth?.rbac?.getUserPermissions?.() || [],
    };
  } catch (error) {
    console.error("Error getting auth status:", error);
    return {
      isAuthenticated: false,
      user: null,
      role: null,
      permissions: [],
    };
  }
};

/**
 * Get current API cache status
 */
export const getCacheStatus = () => {
  try {
    const state = store.getState();
    const apiState = state[baseApi.reducerPath];

    return {
      queries: Object.keys(apiState?.queries || {}).length,
      mutations: Object.keys(apiState?.mutations || {}).length,
      subscriptions: Object.keys(apiState?.subscriptions || {}).length,
      provided: Object.keys(apiState?.provided || {}).length,
    };
  } catch (error) {
    console.error("Error getting cache status:", error);
    return { queries: 0, mutations: 0, subscriptions: 0, provided: 0 };
  }
};

/**
 * Clear all application data (useful for logout/reset)
 */
export const clearApplicationData = () => {
  try {
    // Clear Redux state
    store.dispatch(baseApi.util.resetApiState());

    // Clear localStorage
    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("auth_") ||
        key.startsWith("user_") ||
        key.startsWith("app_")
      ) {
        localStorage.removeItem(key);
      }
    });

    console.log("Application data cleared");
  } catch (error) {
    console.error("Error clearing application data:", error);
  }
};

/**
 * Debug utilities for development
 */
if (APP_CONFIG.IS_DEVELOPMENT) {
  // Make store available globally
  window.store = store;

  // Add debug utilities
  window.storeDebug = {
    getState: () => store.getState(),
    getAuthStatus,
    getCacheStatus,
    clearApplicationData,

    // API utilities
    resetApiCache: () => store.dispatch(baseApi.util.resetApiState()),
    invalidateAllTags: () =>
      store.dispatch(
        baseApi.util.invalidateTags(["User", "Dashboard", "Report", "Settings"])
      ),

    // Auth utilities - FIX: Use proper action creators
    logout: () => store.dispatch({ type: "auth/logout" }),

    // Performance utilities
    measurePerformance: (actionName, actionFn) => {
      const start = performance.now();
      const result = actionFn();
      const end = performance.now();
      console.log(`${actionName}: ${(end - start).toFixed(2)}ms`);
      return result;
    },

    // State inspection
    inspectQueries: () => {
      try {
        const state = store.getState();
        const queries = state[baseApi.reducerPath]?.queries || {};

        console.table(
          Object.entries(queries).map(([key, query]) => ({
            endpoint: key,
            status: query?.status || "unknown",
            data: !!query?.data,
            error: !!query?.error,
            lastUpdated: query?.fulfilledTimeStamp
              ? new Date(query.fulfilledTimeStamp).toLocaleTimeString()
              : "Never",
          }))
        );
      } catch (error) {
        console.error("Error inspecting queries:", error);
      }
    },

    // Subscription monitoring
    inspectSubscriptions: () => {
      try {
        const state = store.getState();
        const subscriptions = state[baseApi.reducerPath]?.subscriptions || {};

        console.log("Active subscriptions:", Object.keys(subscriptions));
        return subscriptions;
      } catch (error) {
        console.error("Error inspecting subscriptions:", error);
        return {};
      }
    },
  };

  // Performance monitoring
  if (APP_CONFIG.PERFORMANCE_MONITORING) {
    let actionCount = 0;
    let slowActions = [];

    const performanceMonitor = (store) => (next) => (action) => {
      const start = performance.now();
      const result = next(action);
      const duration = performance.now() - start;

      actionCount++;

      // Track slow actions
      if (duration > 10) {
        // Actions taking more than 10ms
        slowActions.push({
          type: action.type,
          duration: duration.toFixed(2),
          timestamp: new Date().toISOString(),
        });

        // Keep only last 20 slow actions
        if (slowActions.length > 20) {
          slowActions = slowActions.slice(-20);
        }
      }

      // Log performance summary every 100 actions
      if (actionCount % 100 === 0) {
        console.log(`Performance summary (last 100 actions):`);
        console.log(`   Slow actions: ${slowActions.length}`);
        if (slowActions.length > 0) {
          console.table(slowActions.slice(-10));
        }
      }

      return result;
    };

    // Add performance monitor to debug utilities
    window.storeDebug.getSlowActions = () => slowActions;
    window.storeDebug.clearPerformanceData = () => {
      actionCount = 0;
      slowActions = [];
      console.log("Performance data cleared");
    };
  }

  console.log("Store debug utilities available at window.storeDebug");
}

// ===== STORE PERSISTENCE (Optional) =====
// Uncomment if you want to persist certain parts of the state

/*
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['ui'], // Only persist UI state
  blacklist: ['auth', baseApi.reducerPath], // Don't persist auth or API cache
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Use persistedReducer in store configuration
// export const persistor = persistStore(store);
*/

// ===== ERROR BOUNDARY FOR STORE =====
export const storeErrorHandler = (error, errorInfo) => {
  console.error("Store Error:", error, errorInfo);

  // Log error to external service if configured
  if (window.Sentry) {
    window.Sentry.captureException(error, {
      contexts: {
        redux: {
          state: store.getState(),
        },
      },
    });
  }

  // Clear potentially corrupted state
  if (error.message.includes("state") || error.message.includes("reducer")) {
    console.warn("Clearing potentially corrupted state...");
    clearApplicationData();
  }
};

// ===== STORE HEALTH CHECK =====
export const performStoreHealthCheck = () => {
  try {
    const health = {
      timestamp: new Date().toISOString(),
      store: {
        configured: !!store,
        hasState: !!store.getState(),
      },
      api: {
        configured: !!baseApi,
        middlewareActive: true, // Always true if store is working
      },
      auth: {
        initialized: store.getState().auth?.isInitialized || false,
        authenticated: store.getState().auth?.isAuthenticated || false,
      },
      cache: getCacheStatus(),
      performance: {
        actionCount: window.storeDebug?.actionCount || "N/A",
        slowActions: window.storeDebug?.getSlowActions?.()?.length || "N/A",
      },
    };

    console.log("Store Health Check:", health);
    return health;
  } catch (error) {
    console.error("Error performing health check:", error);
    return {
      error: error.message,
      timestamp: new Date().toISOString(),
    };
  }
};

// Run health check in development
if (APP_CONFIG.IS_DEVELOPMENT) {
  setTimeout(() => {
    performStoreHealthCheck();
  }, 1000);
}

export default store;
